import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitOmniauthRequest, OmniauthProvider } from '../../../../services/omniauthRequest';

const btnClasses = `
  w-full h-[42px] px-[16px]
  rounded-[20px]
  text-[13px] font-medium
  flex items-center justify-center gap-2
  cursor-pointer
  transition-colors
`;

interface SocialConfirmButtonsProps {
  /** Token da URL de confirmação (?token=...), repassado para a sessão do backend. */
  token: string;
}

/**
 * Alternativa de um clique ao formulário manual: confirma a conta associando-a
 * a uma identidade Google/Facebook, sem precisar definir senha.
 *
 * Submete um POST real (via `submitOmniauthRequest`) para
 * `/condomino/auth/:provider` levando `confirmation_token` — o
 * `OmniAuth.config.before_request_phase` (api-customer) guarda esse valor na
 * sessão antes do redirect ao provedor, que é o único jeito de
 * `OmniauthCallbacksController` reconhecer este fluxo de confirmação.
 */
export function SocialConfirmButtons({ token }: SocialConfirmButtonsProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<OmniauthProvider | null>(null);
  const [error, setError] = useState('');

  const go = async (provider: OmniauthProvider) => {
    setError('');
    setPending(provider);
    try {
      await submitOmniauthRequest(provider, { confirmation_token: token });
      // Em caso de sucesso o navegador já saiu desta página (submit real).
    } catch {
      setError(t('views.account_confirmation.social.error', 'Não foi possível iniciar a confirmação. Tente novamente ou use o formulário abaixo.'));
      setPending(null);
    }
  };

  return (
    <div className="mb-5">
      <p className="text-[12px] text-navy-700/70 leading-[1.5] text-center mb-3">
        {t('views.account_confirmation.social.intro', 'Confirme com um clique usando sua conta Google ou Facebook — sem precisar criar uma senha.')}
      </p>

      {error && <p className="text-[11px] text-alert-caution text-center mb-3">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <button
          type="button"
          onClick={() => go('google_oauth2')}
          disabled={pending !== null}
          className={`${btnClasses} bg-white border border-solid border-mist-300 text-navy-800 hover:bg-mist-100 focus:outline-none focus:ring-2 focus:ring-sky-500/30 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <GoogleLogo />
          {pending === 'google_oauth2' ? t('views.account_confirmation.social.redirecting', 'Redirecionando...') : t('views.account_confirmation.social.confirm_google', 'Confirmar com o Google')}
        </button>

        <button
          type="button"
          onClick={() => go('facebook')}
          disabled={pending !== null}
          className={`${btnClasses} border-0 !text-white bg-[#1877F2] hover:bg-[#1465D8] focus:outline-none focus:ring-2 focus:ring-[#1877F2]/40 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <FacebookLogo />
          {pending === 'facebook' ? t('views.account_confirmation.social.redirecting', 'Redirecionando...') : t('views.account_confirmation.social.confirm_facebook', 'Confirmar com o Facebook')}
        </button>
      </div>

      <div className="flex items-center gap-3" role="presentation">
        <div className="h-px flex-1 bg-mist-300" />
        <span className="text-[11px] text-navy-700/60 uppercase tracking-wide">{t('views.login.or', 'ou')}</span>
        <div className="h-px flex-1 bg-mist-300" />
      </div>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  );
}

function FacebookLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#fff"
        d="M12.4 9.7 12.8 7h-2.6V5.3c0-.74.36-1.46 1.53-1.46H13V1.5S11.9 1.3 10.84 1.3c-2.2 0-3.64 1.33-3.64 3.74V7H4.9v2.7h2.3v6.53c.46.07.94.11 1.42.11s.96-.04 1.42-.11V9.7h2.36z"
      />
    </svg>
  );
}
